import { FEATURE } from "@/features";
import { Button } from "@components/ui/button";
import { Feature } from "@components/Feature";
import {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemActions,
} from "@components/ui/item";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChartColumnBigIcon, RefreshCcwIcon } from "lucide-react";
import { SearchFilter } from "@/types/SearchFilter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getTaskStatus, newTask } from "@/api/endpoints/task";
import { formatDateForApi } from "@/utils";
import { useI18n } from "@/contexts/i18nContext";
import { sub } from "date-fns";
import { toast } from "sonner";
import { Spinner } from "@components/ui/spinner";
import { TASK_STATUS } from "@/constants";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";

export default function Analytics({
  reportUuid,
  filters,
}: {
  reportUuid: Nullable<Uuid> | undefined;
  filters: SearchFilter;
}) {
  const { language, currency } = useI18n();
  const { t } = useLingui();

  // Server state
  const queryClient = useQueryClient();

  const taskStatusQueryKey = [reportUuid, "powerpoint"];

  const { mutate: generatePowerpoint, data: taskResponse } = useMutation({
    mutationFn: () => {
      if (!reportUuid) {
        throw new Error();
      }

      postTrackingData(EVENT.GENERATE_PPT);

      return newTask("generate_powerpoint", {
        start_date:
          filters?.start_date ||
          formatDateForApi(sub(new Date(), { months: 3 })),
        end_date: filters?.end_date || formatDateForApi(new Date()),
        insights_report_uuid: reportUuid,
        filters,
        language,
        currency,
      });
    },
    onSuccess: (res) => {
      if (!res.ok) {
        throw new Error();
      }

      queryClient.resetQueries({ queryKey: taskStatusQueryKey });
    },
    onError: () => {
      toast.error(t`Lyckades inte exportera till PowerPoint`);
    },
  });

  const task = taskResponse?.ok ? taskResponse.data : undefined;

  const {
    error: taskStatusError,
    data: taskStatusResponse,
    isLoading: isTaskStatusLoading,
  } = useQuery({
    queryKey: [...taskStatusQueryKey, task?.task_id],
    queryFn: () => {
      if (!task) {
        return null;
      }

      return getTaskStatus(task.task_type, task.task_id);
    },
    enabled: (res) => {
      const taskStatus = res.state.data?.ok ? res.state.data.data : undefined;

      return Boolean(
        task &&
        (!taskStatus ||
          taskStatus?.task_status === TASK_STATUS.PENDING ||
          taskStatus?.task_status === TASK_STATUS.RETRY)
      );
    },
    refetchInterval: 1_000,
  });

  const taskStatus = taskStatusResponse?.ok
    ? taskStatusResponse.data
    : undefined;

  // Handlers
  function handleGeneratePowerPoint() {
    generatePowerpoint();
  }

  return (
    <Feature required={FEATURE.HAS_PPT_FEATURE}>
      <Item variant="outline" size="sm">
        <ItemMedia variant="icon">
          <ChartColumnBigIcon />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>
            <Trans>Analys</Trans>
          </ItemTitle>
        </ItemContent>
        <ItemActions>
          {taskStatus ? (
            taskStatus.task_status === TASK_STATUS.PENDING ||
            taskStatus.task_status === TASK_STATUS.RETRY ? (
              <>
                <p>
                  <Trans>Genererar PowerPoint</Trans>
                </p>
                <Spinner />
              </>
            ) : taskStatus.task_status === TASK_STATUS.FAILURE ? (
              <>
                <p className="text-destructive">
                  <Trans>Någonting gick fel</Trans>
                </p>

                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={handleGeneratePowerPoint}
                >
                  <Trans>Försök igen</Trans>
                  <RefreshCcwIcon />
                </Button>
              </>
            ) : taskStatus.task_status === TASK_STATUS.SUCCESS &&
              typeof taskStatus.data === "string" ? (
              <Button asChild size="sm" type="button">
                <a
                  href={taskStatus.data}
                  onClick={() => {
                    postTrackingData(EVENT.DOWNLOAD_PPT);
                  }}
                >
                  <Trans>Ladda ner PowerPoint</Trans>
                </a>
              </Button>
            ) : null
          ) : isTaskStatusLoading ? (
            <Spinner />
          ) : taskStatusError ? (
            <p className="text-destructive">
              <Trans>Någonting gick fel</Trans>
            </p>
          ) : (
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={handleGeneratePowerPoint}
            >
              <Trans>Exportera till PowerPoint</Trans>
            </Button>
          )}
        </ItemActions>
      </Item>
    </Feature>
  );
}
